import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useSchools } from '../../hooks/useSchools';
import { useStudentPhoto } from '../../hooks/useStudents';
import { 
  Box, 
  Container, 
  Typography, 
  Card, 
  CardContent, 
  CardActionArea, 
  Avatar, 
  IconButton, 
  Tooltip
} from '@mui/material';
import { 
  School as SchoolIcon, 
  ExitToApp as LogoutIcon,
  Class as ClassIcon
} from '@mui/icons-material';
import { colors } from '../../colors';

interface StudentProfileAvatarProps {
  studentId: number;
  studentName: string;
  avatarStyles?: any;
}

const StudentProfileAvatar: React.FC<StudentProfileAvatarProps> = ({ studentId, studentName, avatarStyles }) => {
  const { imageUrl, hasPhoto } = useStudentPhoto(studentId);

  const initials = studentName
    .split(' ')
    .map((n: string) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  if (hasPhoto && imageUrl) {
    return (
      <Avatar 
        src={imageUrl}
        sx={{ 
          ...avatarStyles,
          border: '2px solid rgba(255,255,255,0.8)',
          boxShadow: '0 8px 20px rgba(0,0,0,0.1)'
        }} 
      />
    );
  }

  return (
    <Avatar sx={avatarStyles}>
      {initials}
    </Avatar>
  );
};

export default function StudentProfilePicker() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { useSchoolById } = useSchools(false, { enableQuery: false });
  const { data: school } = useSchoolById(user?.schoolId ?? undefined);

  const schoolImageUrl = school && school.schoolImage 
    ? `${process.env.REACT_APP_API_URL}/schools/school-image/${school.schoolImage}`
    : `/3010ca78-6194-45ee-a965-c6550ad0e444.jpg`;

  const handleSelectStudent = (studentId: number, studentCode?: string) => {
    sessionStorage.setItem('selectedStudentId', String(studentId));
    localStorage.setItem('selectedStudentId', String(studentId));
    if (studentCode) {
      sessionStorage.setItem('selectedStudentCode', studentCode);
      localStorage.setItem('selectedStudentCode', studentCode);
    }
    // Clear classId and sessionId to fetch the selected student's values
    localStorage.removeItem('studentClassId');
    localStorage.removeItem('studentSessionId');
    navigate('/student/dashboard');
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const studentCount = user?.students?.length ?? 0;
  const useHorizontalRow = studentCount >= 3;

  if (!user || !user.students || user.students.length === 0) {
    return (
      <Box 
        sx={{ 
          minHeight: '100vh', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* School image as full background */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            zIndex: 0,
            background: `url(${schoolImageUrl}) center/cover no-repeat`,
          }}
        />
        {/* Gradient overlay */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            zIndex: 0,
            background: 'linear-gradient(135deg, rgba(246, 249, 252, 0.88) 0%, rgba(238, 250, 247, 0.93) 100%)',
          }}
        />
        <Typography variant="h6" sx={{ zIndex: 1 }}>No student profiles found.</Typography>
      </Box>
    );
  }

  return (
    <Box 
      sx={{ 
        minHeight: '100vh', 
        display: 'flex', 
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* School image as full background */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          zIndex: 0,
          background: `url(${schoolImageUrl}) center/cover no-repeat`,
        }}
      />
      {/* Gradient overlay */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          zIndex: 0,
          background: 'linear-gradient(135deg, rgba(246, 249, 252, 0.88) 0%, rgba(238, 250, 247, 0.93) 100%)',
        }}
      />
      {/* Decorative background shapes for rich aesthetics */}
      <Box 
        sx={{
          position: 'absolute',
          top: -200,
          right: -200,
          width: 600,
          height: 600,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(1,104,234,0.08) 0%, rgba(255,255,255,0) 70%)',
          zIndex: 0
        }}
      />
      <Box 
        sx={{
          position: 'absolute',
          bottom: -300,
          left: -300,
          width: 700,
          height: 700,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(1,104,234,0.06) 0%, rgba(255,255,255,0) 70%)',
          zIndex: 0
        }}
      />

      {/* Header bar */}
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          p: 3, 
          zIndex: 1 
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SchoolIcon sx={{ color: colors.primary, fontSize: 32 }} />
          <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1 }}>
            <Typography variant="h6" fontWeight="800" color="text.primary">
              {school?.nameofSchool || 'School ERP'}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.2 }}>
              Select the student profile to continue
            </Typography>
          </Box>
        </Box>
        <Tooltip title="Logout">
          <IconButton 
            onClick={handleLogout} 
            sx={{ 
              backgroundColor: 'rgba(233, 30, 99, 0.08)',
              color: colors.error,
              '&:hover': {
                backgroundColor: 'rgba(233, 30, 99, 0.15)',
              }
            }}
          >
            <LogoutIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Main content */}
      <Container 
        maxWidth={useHorizontalRow ? false : 'md'}
        sx={{ 
          flex: 1, 
          display: 'flex', 
          flexDirection: 'column', 
          justifyContent: 'center', 
          alignItems: 'center',
          pb: 8,
          px: { xs: 2, md: 3 },
          zIndex: 1
        }}
      >
        <Typography 
          variant="h3" 
          component="h1" 
          fontWeight="800" 
          align="center" 
          gutterBottom
          sx={{ 
            background: `linear-gradient(45deg, ${colors.primary} 30%, #00d2ff 90%)`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 2,
            letterSpacing: '-0.5px'
          }}
        >
          Select Student Profile
        </Typography>
        <Typography 
          variant="h6" 
          align="center" 
          color="text.secondary" 
          sx={{ mb: 6, maxWidth: 500, fontWeight: 400 }}
        >
          Welcome! Multiple student accounts are associated with your mobile number. Please choose a profile to proceed to the dashboard.
        </Typography>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            flexWrap: useHorizontalRow ? 'nowrap' : 'wrap',
            gap: 3,
            width: '100%',
            justifyContent: 'center',
            alignItems: 'stretch',
          }}
        >
          {user.students.map((student) => {
            const studentName = (student.studentName || '').trim();

            return (
              <Box
                key={student.id}
                sx={{
                  flex: useHorizontalRow ? `1 1 ${100 / studentCount}%` : '0 0 auto',
                  minWidth: useHorizontalRow ? 0 : undefined,
                  width: useHorizontalRow ? undefined : { xs: '100%', sm: 'calc(50% - 12px)', md: '45%' },
                  maxWidth: useHorizontalRow ? `${100 / studentCount}%` : { xs: '100%', sm: 'calc(50% - 12px)', md: 420 },
                }}
              >
                <Card 
                  sx={{ 
                    height: '100%',
                    borderRadius: 4,
                    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.04)',
                    border: '1px solid rgba(0, 0, 0, 0.04)',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      transform: 'translateY(-6px)',
                      boxShadow: '0 20px 40px rgba(1, 104, 234, 0.12)',
                      borderColor: 'rgba(1, 104, 234, 0.3)',
                    }
                  }}
                >
                  <CardActionArea 
                    onClick={() => handleSelectStudent(student.id, student.studentCode)}
                    sx={{ height: '100%', p: 3 }}
                  >
                    <CardContent sx={{ p: 0, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <StudentProfileAvatar 
                        studentId={student.id}
                        studentName={studentName || 'Student'}
                        avatarStyles={{ 
                          width: useHorizontalRow ? 'clamp(56px, 12vw, 80px)' : 80, 
                          height: useHorizontalRow ? 'clamp(56px, 12vw, 80px)' : 80, 
                          fontSize: useHorizontalRow ? 'clamp(1.1rem, 2.2vw, 1.8rem)' : '1.8rem', 
                          fontWeight: 'bold',
                          mb: 2.5,
                          background: `linear-gradient(135deg, ${colors.primary} 0%, #3a96fb 100%)`,
                          boxShadow: '0 8px 20px rgba(1, 104, 234, 0.25)'
                        }}
                      />
                      <Typography 
                        variant="h5" 
                        fontWeight="700" 
                        color="text.primary" 
                        align="center"
                        gutterBottom
                        sx={{
                          fontSize: useHorizontalRow ? 'clamp(0.85rem, 1.6vw, 1.5rem)' : undefined,
                          lineHeight: 1.2,
                          width: '100%',
                          wordBreak: 'break-word',
                        }}
                      >
                        {studentName || '-'}
                      </Typography>
                      
                      <Box 
                        sx={{ 
                          display: 'flex', 
                          flexDirection: 'column', 
                          gap: 1, 
                          width: '100%', 
                          mt: 2, 
                          pt: 2, 
                          borderTop: '1px solid rgba(0, 0, 0, 0.06)' 
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, color: 'text.secondary', justifyContent: 'center' }}>
                          <ClassIcon sx={{ fontSize: 18, color: colors.primary, flexShrink: 0 }} />
                          <Typography
                            variant="body2"
                            fontWeight="500"
                            sx={{
                              fontSize: useHorizontalRow ? 'clamp(0.7rem, 1.2vw, 0.875rem)' : undefined,
                              textAlign: 'center',
                              wordBreak: 'break-word',
                            }}
                          >
                            Class: {student.className} {student.section ? `- ${student.section}` : ''}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Box>
            );
          })}
        </Box>
      </Container>
    </Box>
  );
}
